import PropTypes from 'prop-types';
import React from 'react';

/**
 * Hidden live-region boundary for announcing chatbot events.
 * The visible transcript is kept navigable, while this component is responsible
 * for ordered narration so assistive technology does not drop rapid updates.
 * Entries must not accumulate: JAWS re-reads the whole polite region on each
 * addition, so the owner prunes already-captured entries in the same update
 * that appends a new one.
 *
 * @param {{ politeAnnouncements?: { id: string, text: string }[], assertiveAnnouncement?: string }} props
 * @returns {JSX.Element}
 */
export default function ChatScreenReaderAnnouncer({
  assertiveAnnouncement = '',
  politeAnnouncements = [],
}) {
  return (
    <div className="sr-only" data-testid="chat-sr-announcer">
      <div
        aria-atomic="false"
        aria-live="polite"
        aria-relevant="additions"
        data-testid="chat-sr-polite"
        role="log"
      >
        {politeAnnouncements.map(announcement => (
          <p
            data-testid="chat-sr-polite-message"
            key={announcement.id}
            className="vads-u-margin--0"
          >
            {announcement.text}
          </p>
        ))}
      </div>
      <div
        aria-atomic="true"
        aria-live="assertive"
        data-testid="chat-sr-assertive"
        role="alert"
      >
        {assertiveAnnouncement}
      </div>
    </div>
  );
}

ChatScreenReaderAnnouncer.propTypes = {
  assertiveAnnouncement: PropTypes.string,
  politeAnnouncements: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
    }),
  ),
};
